import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { LazyMotion, domAnimation } from "framer-motion";
import { Button } from "../../../../../../components/ui/Button";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { useSelector } from "react-redux";
import AnimatedMiniPlayer from "./AnimatedMiniPlayer";
import { useUpdateLiveAnimationSettingsMutation } from "../../../../../../queries";
import { ALL_DIRECTIONS, ANIM_TYPES } from "../../../../../../config/campaigns";

const toOptions = (arr) => arr.map((v) => ({ label: v, value: v }));
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const sanitizeAnim = (raw) => {
    const type = raw?.type ?? "FADE";
    const enabled = !!raw?.enabled;
    const duration = Number.isFinite(raw?.duration) ? clamp(raw.duration, 0.2, 2.0) : 0.5;

    const allowed = (() => {
        if (type === "FADE" || type === "DISSOLVE") return [];
        if (type === "FLIP") return ALL_DIRECTIONS.FLIP;
        if (type === "SLIDE") return ALL_DIRECTIONS.SLIDE;
        if (type === "ZOOM") return ALL_DIRECTIONS.ZOOM;
        if (type === "SCROLL") return ALL_DIRECTIONS.SCROLL;
        if (type === "ROLL") return ALL_DIRECTIONS.ROLL;
        return [];
    })();

    let direction =
        type === "FADE" || type === "DISSOLVE"
            ? "CENTER"
            : raw?.direction && allowed.includes(raw.direction)
                ? raw.direction
                : type === "FLIP"
                    ? "HORIZONTAL"
                    : "LEFT";

    return { enabled, type, duration, direction };
};

const layerToSettingsKey = {
    MAIN: "media_animation",
    BANNER: "banner_media_animation",
    CORNER: "corner_media_animation",
    PLAYGROUND: "playground_media_animation",
};

const directionOptionsFor = (type) => {
    if (type === "FADE" || type === "DISSOLVE") return [];
    if (type === "FLIP") return toOptions(ALL_DIRECTIONS.FLIP);
    if (type === "SLIDE") return toOptions(ALL_DIRECTIONS.SLIDE);
    if (type === "ZOOM") return toOptions(ALL_DIRECTIONS.ZOOM);
    if (type === "SCROLL") return toOptions(ALL_DIRECTIONS.SCROLL);
    if (type === "ROLL") return toOptions(ALL_DIRECTIONS.ROLL);
    return [];
};

const MediaAnimationForm = ({ campaignId }) => {
    const { t } = useTranslation();
    const { campaignDetails, layer } = useSelector((s) => s.global) ?? {};
    const animMutation = useUpdateLiveAnimationSettingsMutation();

    const activeLayer = ["MAIN", "BANNER", "CORNER", "PLAYGROUND"].includes(layer) ? layer : "MAIN";

    const initialState = useMemo(() => {
        const key = layerToSettingsKey[activeLayer];
        const raw = campaignDetails?.settings?.live?.[key];
        return sanitizeAnim(raw);
    }, [campaignDetails, activeLayer]);

    const [state, setState] = useState(initialState);

    useEffect(() => {
        setState(initialState);
    }, [initialState]);

    const setField = (patch) => {
        setState((prev) => {
            const next = sanitizeAnim({ ...prev, ...patch });
            if (patch.type) {
                const allowed = directionOptionsFor(next.type).map((o) => o.value);
                if (allowed.length === 0) next.direction = "CENTER";
                else if (!allowed.includes(next.direction))
                    next.direction = next.type === "FLIP" ? "HORIZONTAL" : "LEFT";
            }
            return next;
        });
    };

    const handleSaveAnimation = useCallback(async () => {
        if (!campaignId) return;
        const key = layerToSettingsKey[activeLayer];
        const format = (s) => ({
            enabled: s.enabled,
            type: s.type,
            duration: Number(clamp(s.duration, 0.2, 2.0)),
            direction: s.type === "FADE" || s.type === "DISSOLVE" ? "CENTER" : s.direction,
        });
        const payload = { all_layer: false, [key]: format(state) };
        await animMutation.mutateAsync({ id: campaignId, data: payload });
    }, [campaignId, activeLayer, state, animMutation]);

    const dirOptions =
        state.type === "FADE" || state.type === "DISSOLVE" ? [] : directionOptionsFor(state.type);

    return (
        <div className="flex flex-col md:flex-row w-full h-full">
            {/* Preview */}
            <div className="w-full md:w-2/3 border-b md:border-b-0 md:border-r surface-border flex items-center justify-center p-3">
                <LazyMotion features={domAnimation}>
                    <AnimatedMiniPlayer
                        enabled={state.enabled}
                        type={state.type}
                        direction={state.direction}
                        durationMs={state.duration * 1000}
                    />
                </LazyMotion>
            </div>

            {/* Controls */}
            <div className="w-full md:w-1/3 flex flex-col">
                {/* scrollable body on small screens */}
                <div className="flex-1 overflow-auto max-h-[55vh] md:max-h-none p-3">
                    <label className="text-sm block mb-2">
                        {t('show.show_edit_animation_for_layer', { layer: activeLayer })}
                    </label>

                    <div className="rounded border surface-border p-3">
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <label className="text-sm">{t('show.show_enable_animation')}</label>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-500">{state.enabled ? t('show.show_on') : t('show.show_off')}</span>
                                <InputSwitch
                                    checked={state.enabled}
                                    onChange={(e) => setField({ enabled: !!e.value })}
                                />
                            </div>
                        </div>

                        {state.enabled && (
                            <div className="grid grid-cols-1 gap-4">
                                <Dropdown
                                    value={state.type}
                                    options={ANIM_TYPES}
                                    onChange={(e) => setField({ type: e.value })}
                                    className="w-full"
                                    placeholder={t('show.show_select_animation_type')}
                                    disabled={!state.enabled}
                                />

                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                                    <label className="lg:w-1/2">{t('show.show_duration_sec')}</label>
                                    <InputNumber
                                        value={state.duration}
                                        onValueChange={(e) =>
                                            setField({ duration: clamp(Number(e.value ?? 0.5), 0.2, 2.0) })
                                        }
                                        mode="decimal"
                                        minFractionDigits={1}
                                        maxFractionDigits={1}
                                        step={0.5}
                                        min={0.5}
                                        max={2.0}
                                        showButtons
                                        buttonLayout="stacked"
                                        incrementButtonIcon="pi pi-chevron-up"
                                        decrementButtonIcon="pi pi-chevron-down"
                                        className="w-full lg:w-1/2"
                                        disabled={!state.enabled}
                                    />
                                </div>

                                {state.type !== "FADE" && state.type !== "DISSOLVE" && (
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                                        <label className="lg:w-1/2">{t('show.show_direction')}</label>
                                        <Dropdown
                                            value={state.direction}
                                            options={dirOptions}
                                            onChange={(e) => setField({ direction: e.value })}
                                            className="w-full lg:w-1/2 text-sm"
                                            placeholder={t('show.show_select_direction')}
                                            disabled={!state.enabled}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* sticky footer */}
                <div className="border-t surface-border p-2 flex justify-end sticky bottom-0 bg-white">
                    <Button
                        label={t('show.show_save_and_apply')}
                        className="py-1 w-full md:w-auto"
                        onClick={handleSaveAnimation}
                        disabled={animMutation.isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default MediaAnimationForm;
